/**
 * User Management Component - Create, Edit, and Manage Users
 */
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Typography,
  CircularProgress,
  Tabs,
  Tab,
  TablePagination,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  ClearAll as ClearAllIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import useAuthStore from '../store/authStore';
import { useTranslation } from 'react-i18next';
import config from '../config';
import { getCommonErrorMessage } from '../utils/errorHandler';

const API_BASE_URL = config.apiUrl;

const POSITION_OPTION_VALUES = [
  { key: 'staff', value: 'Staff' },
  { key: 'senior_staff', value: 'Senior Staff' },
  { key: 'lead', value: 'Lead' },
  { key: 'manager', value: 'Manager' },
  { key: 'senior_manager', value: 'Senior Manager' },
  { key: 'director', value: 'Director' },
  { key: 'vp', value: 'VP' },
  { key: 'c_level', value: 'C-level' },
];

const TITLE_OPTION_VALUES = [
  { key: 'engineer', value: 'Engineer' },
  { key: 'hr_specialist', value: 'HR Specialist' },
  { key: 'sales_executive', value: 'Sales Executive' },
  { key: 'analyst', value: 'Analyst' },
  { key: 'coordinator', value: 'Coordinator' },
  { key: 'consultant', value: 'Consultant' },
  { key: 'administrator', value: 'Administrator' },
];

const COUNTRY_OPTION_VALUES = [
  { key: 'vietnam', value: 'Vietnam' },
  { key: 'usa', value: 'United States' },
  { key: 'japan', value: 'Japan' },
  { key: 'singapore', value: 'Singapore' },
  { key: 'other', value: 'Other' },
];

export default function UserManagement() {
  const { token } = useAuthStore();
  const { t } = useTranslation(['common', 'admin']);
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  // Dialog state
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('create'); // 'create' or 'edit'
  const [selectedUser, setSelectedUser] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    department_code: 'HR',
    role: '',
  });

  const [createTab, setCreateTab] = useState('USER');

  // Employee info state
  const [employeeInfo, setEmployeeInfo] = useState({
    employee_code: '',
    phone: '',
    dob: '',
    gender: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    country: '',
    postal_code: '',
    position: '',
    title: '',
    manager_id: '',
    hire_date: '',
    contract_type: '',
  });

  // List view: filters, search, pagination
  const [searchText, setSearchText] = useState('');
  const [filterRole, setFilterRole] = useState(''); // '', USER, ADMIN, MASTER
  const [filterDept, setFilterDept] = useState(''); // department code
  const [filterStatus, setFilterStatus] = useState(''); // '', active, inactive
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);
  const [showPassword, setShowPassword] = useState(false);

  // Option lists for selects
  const genderOptions = useMemo(
    () => [
      { label: t('userManagement.employee.options.gender.male', { ns: 'admin' }), value: 'male' },
      { label: t('userManagement.employee.options.gender.female', { ns: 'admin' }), value: 'female' },
      { label: t('userManagement.employee.options.gender.other', { ns: 'admin' }), value: 'other' },
    ],
    [t],
  );
  const contractOptions = useMemo(
    () => [
      { label: t('userManagement.employee.options.contract.full_time', { ns: 'admin' }), value: 'full_time' },
      { label: t('userManagement.employee.options.contract.part_time', { ns: 'admin' }), value: 'part_time' },
      { label: t('userManagement.employee.options.contract.intern', { ns: 'admin' }), value: 'intern' },
      { label: t('userManagement.employee.options.contract.contractor', { ns: 'admin' }), value: 'contractor' },
      { label: t('userManagement.employee.options.contract.probation', { ns: 'admin' }), value: 'probation' },
    ],
    [t],
  );
  const positionOptions = useMemo(
    () =>
      POSITION_OPTION_VALUES.map(({ key, value }) => ({
        label: t(`userManagement.employee.options.position.${key}`, { ns: 'admin' }),
        value,
      })),
    [t],
  );
  const titleOptions = useMemo(
    () =>
      TITLE_OPTION_VALUES.map(({ key, value }) => ({
        label: t(`userManagement.employee.options.title.${key}`, { ns: 'admin' }),
        value,
      })),
    [t],
  );
  const countryOptions = useMemo(
    () =>
      COUNTRY_OPTION_VALUES.map(({ key, value }) => ({
        label: t(`userManagement.employee.options.country.${key}`, { ns: 'admin' }),
        value,
      })),
    [t],
  );
  const roleOptions = useMemo(
    () => [
      { value: 'USER', label: t('userManagement.roles.user', { ns: 'admin' }) },
      { value: 'ADMIN', label: t('userManagement.roles.admin', { ns: 'admin' }) },
      { value: 'MASTER', label: t('userManagement.roles.master', { ns: 'admin' }) },
    ],
    [t],
  );
  const statusOptions = useMemo(
    () => [
      { value: 'active', label: t('status.state.active') },
      { value: 'inactive', label: t('status.state.inactive') },
    ],
    [t],
  );

  const getRoleLabel = (role) => {
    if (!role) return '';
    const translation = t(`userManagement.roles.${String(role).toLowerCase()}`, { ns: 'admin' });
    return translation || role;
  };

  const getStatusChip = (isActive) => ({
    label: isActive ? t('status.state.active') : t('status.state.inactive'),
    color: isActive ? 'success' : 'default',
  });

  useEffect(() => {
    loadDepartments();
  }, []);

  useEffect(() => {
    loadUsers();
  }, [page, rowsPerPage, filterRole, filterDept, filterStatus]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setPage(0);
      loadUsers();
    }, 300);

    return () => clearTimeout(handler);
  }, [searchText]);

  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.append('page', (page + 1).toString());
      params.append('page_size', rowsPerPage.toString());
      if (searchText) params.append('search', searchText);
      if (filterRole) params.append('role', filterRole);
      if (filterDept) params.append('department', filterDept);
      if (filterStatus) params.append('status', filterStatus);

      const response = await fetch(`${API_BASE_URL}/admin/users?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Load users error detail:', errorData.detail);
        throw new Error(t('userManagement.alerts.loadUsersError', { ns: 'admin' }));
      }

      const data = await response.json();
      setUsers(data.users);
      setTotalUsers(data.total || data.users.length);
    } catch (err) {
      console.error('Load users error:', err);
      setError(t('userManagement.alerts.loadUsersError', { ns: 'admin' }));
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setSearchText('');
    setFilterRole('');
    setFilterDept('');
    setFilterStatus('');
    setPage(0);
  };

  const loadDepartments = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/departments`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error(t('userManagement.alerts.loadDepartmentsError', { ns: 'admin' }));

      const data = await response.json();
      setDepartments(data.departments);
    } catch (err) {
      console.error('Failed to load departments:', err);
    }
  };

  const handleOpenCreate = () => {
    setDialogMode('create');
    setCreateTab('USER');
    setFormData({
      email: '',
      password: '',
      full_name: '',
      department_code: 'HR',
      role: 'USER',
    });
    setShowPassword(false);
    setEmployeeInfo({
      employee_code: '',
      phone: '',
      dob: '',
      gender: '',
      address_line1: '',
      address_line2: '',
      city: '',
      state: '',
      country: '',
      postal_code: '',
      position: '',
      title: '',
      manager_id: '',
      hire_date: '',
      contract_type: '',
    });
    setOpenDialog(true);
  };

  const handleOpenEdit = (user) => {
    setDialogMode('edit');
    setSelectedUser(user);
    setCreateTab(user.role || 'USER');
    setFormData({
      email: user.email,
      password: '', // Leave empty for edit
      full_name: user.full_name,
      department_code: user.department,
      role: user.role,
    });
    setShowPassword(false);
    // Load employee information if exists
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/admin/users/${user.id}/employee-info`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          const info = data.employee_info || {};
          setEmployeeInfo({
            employee_code: info.employee_code || '',
            phone: info.phone || '',
            dob: info.dob || '',
            gender: info.gender || '',
            address_line1: info.address_line1 || '',
            address_line2: info.address_line2 || '',
            city: info.city || '',
            state: info.state || '',
            country: info.country || '',
            postal_code: info.postal_code || '',
            position: info.position || '',
            title: info.title || '',
            manager_id: info.manager_id || '',
            hire_date: info.hire_date || '',
            contract_type: info.contract_type || '',
          });
        } else {
          // If not found (404), reset fields for editing/creation
          setEmployeeInfo({
            employee_code: '',
            phone: '',
            dob: '',
            gender: '',
            address_line1: '',
            address_line2: '',
            city: '',
            state: '',
            country: '',
            postal_code: '',
            position: '',
            title: '',
            manager_id: '',
            hire_date: '',
            contract_type: '',
          });
        }
      } catch (e) {
        // Ignore fetch error to not block editing user basic fields
      }
    })();
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedUser(null);
    setCreateTab('USER');
    setFormData({
      email: '',
      password: '',
      full_name: '',
      department_code: 'HR',
      role: '',
    });
    setShowPassword(false);
    setEmployeeInfo({
      employee_code: '',
      phone: '',
      dob: '',
      gender: '',
      address_line1: '',
      address_line2: '',
      city: '',
      state: '',
      country: '',
      postal_code: '',
      position: '',
      title: '',
      manager_id: '',
      hire_date: '',
      contract_type: '',
    });
    setValidationErrors({});
  };

  const validateForm = () => {
    const errs = {};
    const emailRegex = /^\S+@\S+\.\S+$/;
    if (!formData.email) errs.email = t('userManagement.validation.emailRequired', { ns: 'admin' });
    else if (!emailRegex.test(formData.email)) errs.email = t('userManagement.validation.emailInvalid', { ns: 'admin' });
    if (dialogMode === 'create' && !formData.password) errs.password = t('userManagement.validation.passwordRequired', { ns: 'admin' });
    if (dialogMode === 'create' && formData.password && formData.password.length < 6) errs.password = t('userManagement.validation.passwordMin', { ns: 'admin' });
    if (!formData.full_name) errs.full_name = t('userManagement.validation.fullNameRequired', { ns: 'admin' });
    if (!formData.department_code) errs.department_code = t('userManagement.validation.departmentRequired', { ns: 'admin' });
    if (dialogMode === 'edit' && !formData.role) errs.role = t('userManagement.validation.roleRequired', { ns: 'admin' });
    if (employeeInfo.phone && !/^\+?[0-9\-\s]{6,}$/.test(employeeInfo.phone)) errs.phone = t('userManagement.validation.phoneInvalid', { ns: 'admin' });
    setValidationErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    setError(null);
    setSuccess(null);

    if (!validateForm()) {
      setError(t('userManagement.alerts.validationError', { ns: 'admin' }));
      return;
    }

    try {
      if (dialogMode === 'create') {
        // Create new user
        const { role: _role, ...createPayload } = formData;
        const response = await fetch(`${API_BASE_URL}/admin/users`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ ...createPayload, role: createTab }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.error('Create user error detail:', errorData.detail);
          throw new Error(t('userManagement.alerts.createError', { ns: 'admin' }));
        }
        const created = await response.json();
        const userId = created?.user?.id || created?.id;

        // Build employee info payload from optional fields
        // Filter out empty strings to avoid validation errors (e.g., dob, hire_date must be null or valid date)
        const empPayload = Object.entries({
          ...employeeInfo,
          department_code: formData.department_code || undefined,
        }).reduce((acc, [key, value]) => {
          if (value !== '' && value !== null && value !== undefined) {
            acc[key] = value;
          }
          return acc;
        }, {});
        const hasEmpData = Object.keys(empPayload).length > 0;

        if (userId && hasEmpData) {
          const empRes = await fetch(`${API_BASE_URL}/admin/users/${userId}/employee-info`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(empPayload),
          });
          if (!empRes.ok) {
            const errorData = await empRes.json().catch(() => ({}));
            console.error('Save employee info error detail:', errorData.detail);
            throw new Error(t('userManagement.alerts.employeeInfoSaveError', { ns: 'admin' }));
          }
        }

        setSuccess(t('userManagement.alerts.createSuccess', { ns: 'admin' }));
      } else {
        // Update existing user
        const { role, password, full_name, department_code } = formData;
        const updateData = {
          full_name,
          department_code,
        };

        // Only include password if it's changed
        if (password) {
          updateData.password = password;
        }
        if (role) {
          updateData.role = role;
        }

        const response = await fetch(`${API_BASE_URL}/admin/users/${selectedUser.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(updateData),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.error('Update user error detail:', errorData.detail);
          throw new Error(t('userManagement.alerts.updateError', { ns: 'admin' }));
        }
        // Upsert employee info (PUT then fallback to POST if not found)
        try {
          // Filter out empty strings to avoid validation errors (e.g., dob, hire_date must be null or valid date)
          const empPayload = Object.entries({
            ...employeeInfo,
            department_code: formData.department_code || undefined,
          }).reduce((acc, [key, value]) => {
            if (value !== '' && value !== null && value !== undefined) {
              acc[key] = value;
            }
            return acc;
          }, {});
          const hasEmpData = Object.keys(empPayload).length > 0;
          if (hasEmpData) {
            const putRes = await fetch(`${API_BASE_URL}/admin/users/${selectedUser.id}/employee-info`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify(empPayload),
            });
            if (!putRes.ok) {
              // If not found create
              if (putRes.status === 404) {
                const postRes = await fetch(`${API_BASE_URL}/admin/users/${selectedUser.id}/employee-info`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                  },
                  body: JSON.stringify(empPayload),
                });
                if (!postRes.ok) {
                  const errData = await postRes.json().catch(() => ({}));
                  console.error('Post employee info error detail:', errData.detail);
                  throw new Error(t('userManagement.alerts.employeeInfoSaveError', { ns: 'admin' }));
                }
              } else {
                const errData = await putRes.json().catch(() => ({}));
                console.error('Put employee info error detail:', errData.detail);
                throw new Error(t('userManagement.alerts.employeeInfoUpdateError', { ns: 'admin' }));
              }
            }
          }
        } catch (empErr) {
          throw empErr;
        }

        setSuccess(t('userManagement.alerts.updateSuccess', { ns: 'admin' }));
      }

      handleCloseDialog();
      await loadUsers();
    } catch (err) {
      setError(err.message || t('userManagement.alerts.validationError', { ns: 'admin' }));
    }
  };

  const handleDelete = async (userId, userEmail) => {
    if (!window.confirm(t('userManagement.confirmations.deactivateUser', { ns: 'admin', email: userEmail }))) {
      return;
    }

    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(`${API_BASE_URL}/admin/users/${userId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Deactivate user error detail:', errorData.detail);
        throw new Error(t('userManagement.alerts.deactivateError', { ns: 'admin' }));
      }

      setSuccess(t('userManagement.alerts.deactivateSuccess', { ns: 'admin' }));
      await loadUsers();
    } catch (err) {
      console.error('Deactivate user error:', err);
      setError(t('userManagement.alerts.deactivateError', { ns: 'admin' }));
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'MASTER':
        return 'error';
      case 'ADMIN':
        return 'warning';
      default:
        return 'default';
    }
  };

  const hasActiveFilters = Boolean(searchText || filterRole || filterDept || filterStatus || page);

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          mb: 3,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #eef2ff 0%, #fdf2f8 100%)',
          boxShadow: '0 12px 30px rgba(102, 126, 234, 0.18)',
          p: { xs: 2.5, md: 3 },
          display: 'flex',
          flexDirection: 'column',
          gap: 2.5,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', md: 'center' },
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
              👥 {t('userManagement.title', { ns: 'admin' })}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('userManagement.subtitle', { ns: 'admin' })}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreate}
            sx={{
              px: 3,
              py: 1.2,
              borderRadius: 999,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              boxShadow: '0px 10px 20px rgba(118, 75, 162, 0.25)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fdd 0%, #6b4298 100%)',
                boxShadow: '0px 12px 24px rgba(118, 75, 162, 0.3)',
              },
            }}
          >
            {t('userManagement.createButton', { ns: 'admin' })}
          </Button>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1.5,
            alignItems: 'center',
          }}
        >
          <TextField
            size="small"
            placeholder={t('userManagement.filters.searchPlaceholder', { ns: 'admin' })}
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              setPage(0);
            }}
            onBlur={loadUsers}
            InputProps={{ startAdornment: <InputAdornment position="start">🔎</InputAdornment> }}
            sx={{
              minWidth: { xs: '100%', sm: 220 },
              flexGrow: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: '#fff',
              },
            }}
          />
          <FormControl
            size="small"
            sx={{
              minWidth: { xs: '100%', sm: 130 },
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: '#fff',
              },
            }}
          >
            <InputLabel>{t('userManagement.filters.role', { ns: 'admin' })}</InputLabel>
            <Select
              value={filterRole}
              label={t('userManagement.filters.role', { ns: 'admin' })}
              onChange={(e) => {
                setFilterRole(e.target.value);
                setPage(0);
              }}
              onClose={loadUsers}
            >
              <MenuItem value="">
                <em>{t('common.all')}</em>
              </MenuItem>
              {roleOptions.map(({ value, label }) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl
            size="small"
            sx={{
              minWidth: { xs: '100%', sm: 160 },
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: '#fff',
              },
            }}
          >
            <InputLabel>{t('userManagement.filters.department', { ns: 'admin' })}</InputLabel>
            <Select
              value={filterDept}
              label={t('userManagement.filters.department', { ns: 'admin' })}
              onChange={(e) => {
                setFilterDept(e.target.value);
                setPage(0);
              }}
              onClose={loadUsers}
            >
              <MenuItem value="">
                <em>{t('common.all')}</em>
              </MenuItem>
              {departments.map((d) => (
                <MenuItem key={d.code} value={d.code}>
                  {d.name} ({d.code})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl
            size="small"
            sx={{
              minWidth: { xs: '100%', sm: 150 },
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: '#fff',
              },
            }}
          >
            <InputLabel>{t('userManagement.filters.status', { ns: 'admin' })}</InputLabel>
            <Select
              value={filterStatus}
              label={t('userManagement.filters.status', { ns: 'admin' })}
              onChange={(e) => {
                setFilterStatus(e.target.value);
                setPage(0);
              }}
              onClose={loadUsers}
            >
              <MenuItem value="">
                <em>{t('common.all')}</em>
              </MenuItem>
              {statusOptions.map(({ value, label }) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => {
              setPage(0);
              loadUsers();
            }}
            disabled={loading}
            sx={{
              borderRadius: 999,
              px: 2.5,
              py: 1,
              borderColor: 'rgba(102, 126, 234, 0.45)',
              color: '#5a6fdd',
              '&:hover': {
                borderColor: 'rgba(102, 126, 234, 0.7)',
                backgroundColor: 'rgba(102, 126, 234, 0.06)',
              },
            }}
          >
            {t('userManagement.buttons.refresh', { ns: 'admin' })}
          </Button>
          <Button
            variant="text"
            startIcon={<ClearAllIcon />}
            onClick={handleClearFilters}
            disabled={!hasActiveFilters || loading}
            sx={{
              borderRadius: 999,
              px: 2.5,
              py: 1,
              color: '#6b7280',
              '&:hover': {
                backgroundColor: 'rgba(102, 126, 234, 0.08)',
                color: '#4c51bf',
              },
            }}
          >
            {t('userManagement.buttons.clear', { ns: 'admin' })}
          </Button>
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Users Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={2}>
          <Table>
            <TableHead sx={{ bgcolor: '#f5f7fa' }}>
              <TableRow>
                <TableCell><strong>{t('userManagement.table.email', { ns: 'admin' })}</strong></TableCell>
                <TableCell><strong>{t('userManagement.table.fullName', { ns: 'admin' })}</strong></TableCell>
                <TableCell><strong>{t('userManagement.table.department', { ns: 'admin' })}</strong></TableCell>
                <TableCell><strong>{t('userManagement.table.role', { ns: 'admin' })}</strong></TableCell>
                <TableCell><strong>{t('userManagement.table.status', { ns: 'admin' })}</strong></TableCell>
                <TableCell><strong>{t('userManagement.table.created', { ns: 'admin' })}</strong></TableCell>
                <TableCell align="right"><strong>{t('userManagement.table.actions', { ns: 'admin' })}</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.full_name}</TableCell>
                  <TableCell>
                    <Chip label={user.department} size="small" />
                  </TableCell>
                  <TableCell>
                    <Chip label={getRoleLabel(user.role)} color={getRoleColor(user.role)} size="small" />
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const status = getStatusChip(user.is_active);
                      return <Chip label={status.label} color={status.color} size="small" />;
                    })()}
                  </TableCell>
                  <TableCell>{new Date(user.created_at).toLocaleDateString()}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleOpenEdit(user)}
                      title={t('userManagement.tooltips.edit', { ns: 'admin' })}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(user.id, user.email)}
                      title={t('userManagement.tooltips.deactivate', { ns: 'admin' })}
                      disabled={!user.is_active}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={totalUsers}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </TableContainer>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialogMode === 'create' ? '➕ Create New User' : '✏️ Edit User'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            {dialogMode === 'create' && (
              <Tabs
                value={createTab}
                onChange={(_, value) => {
                  setCreateTab(value);
                  setFormData({ ...formData, role: value });
                }}
                variant="fullWidth"
                sx={{ mb: 1, borderRadius: 2, bgcolor: 'rgba(102, 126, 234, 0.08)' }}
              >
                <Tab label={t('userManagement.roles.user', { ns: 'admin' })} value="USER" />
                <Tab label={t('userManagement.roles.admin', { ns: 'admin' })} value="ADMIN" />
                <Tab label={t('userManagement.roles.master', { ns: 'admin' })} value="MASTER" />
              </Tabs>
            )}
            <TextField
              label={t('placeholders.email')}
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              fullWidth
              disabled={dialogMode === 'edit'} // Can't change email
              error={!!validationErrors.email}
              helperText={validationErrors.email}
            />

            <TextField
              label={dialogMode === 'create'
                ? t('userManagement.dialog.passwordLabelCreate', { ns: 'admin' })
                : t('userManagement.dialog.passwordLabelEdit', { ns: 'admin' })}
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required={dialogMode === 'create'}
              fullWidth
              error={!!validationErrors.password}
              helperText={validationErrors.password}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((prev) => !prev)}
                      onMouseDown={(e) => e.preventDefault()}
                      edge="end"
                      aria-label={showPassword ? t('userManagement.dialog.passwordLabelCreate', { ns: 'admin' }) : t('userManagement.dialog.passwordLabelCreate', { ns: 'admin' })}
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              label={t('userManagement.table.fullName', { ns: 'admin' })}
              value={formData.full_name}
              onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              required
              fullWidth
              error={!!validationErrors.full_name}
              helperText={validationErrors.full_name}
            />

            <FormControl fullWidth error={!!validationErrors.department_code}>
              <InputLabel>{t('userManagement.table.department', { ns: 'admin' })}</InputLabel>
              <Select
                value={formData.department_code}
                label={t('userManagement.table.department', { ns: 'admin' })}
                onChange={(e) => setFormData({ ...formData, department_code: e.target.value })}
              >
                {departments.map((dept) => (
                  <MenuItem key={dept.code} value={dept.code}>
                    {dept.name} ({dept.code})
                  </MenuItem>
                ))}
              </Select>
              {validationErrors.department_code && (
                <Typography variant="caption" color="error">{validationErrors.department_code}</Typography>
              )}
            </FormControl>

            {dialogMode === 'create' ? (
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 1,
                  px: 2,
                  py: 1.25,
                  borderRadius: 2,
                  backgroundColor: 'rgba(102, 126, 234, 0.08)',
                }}
              >
                <Typography variant="subtitle2" sx={{ mr: 1 }}>
                  {t('userManagement.fields.role', { ns: 'admin' })}
                </Typography>
                <Chip
                  label={getRoleLabel(createTab)}
                  color={getRoleColor(createTab)}
                  size="small"
                />
                <Typography variant="caption" color="text.secondary">
                  {t('userManagement.dialog.roleInfo', { ns: 'admin' })}
                </Typography>
              </Box>
            ) : (
              <FormControl fullWidth error={!!validationErrors.role}>
                <InputLabel>{t('userManagement.fields.role', { ns: 'admin' })}</InputLabel>
                <Select
                  value={formData.role}
                  label={t('userManagement.fields.role', { ns: 'admin' })}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                >
                  {roleOptions.map(({ value, label }) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
                {validationErrors.role && (
                  <Typography variant="caption" color="error">{validationErrors.role}</Typography>
                )}
              </FormControl>
            )}

            {/* Employee Information Section (shown for both create and edit) */}
            <Typography variant="subtitle1" sx={{ mt: 1, fontWeight: 700 }}>
              👤 {t('userManagement.employee.title', { ns: 'admin' })}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                <TextField
                  label={t('userManagement.employee.fields.employeeCode', { ns: 'admin' })}
                  value={employeeInfo.employee_code}
                  onChange={(e) => setEmployeeInfo({ ...employeeInfo, employee_code: e.target.value })}
                  fullWidth
                />
                <TextField
                  label={t('userManagement.employee.fields.phone', { ns: 'admin' })}
                  value={employeeInfo.phone}
                  onChange={(e) => setEmployeeInfo({ ...employeeInfo, phone: e.target.value })}
                  fullWidth
                  error={!!validationErrors.phone}
                  helperText={validationErrors.phone}
                />
                <TextField
                  label={t('userManagement.employee.fields.dob', { ns: 'admin' })}
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={employeeInfo.dob}
                  onChange={(e) => setEmployeeInfo({ ...employeeInfo, dob: e.target.value })}
                  fullWidth
                />
              <FormControl fullWidth>
                <InputLabel>{t('userManagement.employee.fields.gender', { ns: 'admin' })}</InputLabel>
                <Select
                  value={employeeInfo.gender}
                  label={t('userManagement.employee.fields.gender', { ns: 'admin' })}
                  onChange={(e) => setEmployeeInfo({ ...employeeInfo, gender: e.target.value })}
                >
                  <MenuItem value=""><em>{t('common.none')}</em></MenuItem>
                  {genderOptions.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
                <TextField
                  label={t('userManagement.employee.fields.address1', { ns: 'admin' })}
                  value={employeeInfo.address_line1}
                  onChange={(e) => setEmployeeInfo({ ...employeeInfo, address_line1: e.target.value })}
                  fullWidth
                />
                <TextField
                  label={t('userManagement.employee.fields.address2', { ns: 'admin' })}
                  value={employeeInfo.address_line2}
                  onChange={(e) => setEmployeeInfo({ ...employeeInfo, address_line2: e.target.value })}
                  fullWidth
                />
                <TextField
                  label={t('userManagement.employee.fields.city', { ns: 'admin' })}
                  value={employeeInfo.city}
                  onChange={(e) => setEmployeeInfo({ ...employeeInfo, city: e.target.value })}
                  fullWidth
                />
                <TextField
                  label={t('userManagement.employee.fields.state', { ns: 'admin' })}
                  value={employeeInfo.state}
                  onChange={(e) => setEmployeeInfo({ ...employeeInfo, state: e.target.value })}
                  fullWidth
                />
              <FormControl fullWidth>
                <InputLabel>{t('userManagement.employee.fields.country', { ns: 'admin' })}</InputLabel>
                <Select
                  value={employeeInfo.country}
                  label={t('userManagement.employee.fields.country', { ns: 'admin' })}
                  onChange={(e) => setEmployeeInfo({ ...employeeInfo, country: e.target.value })}
                >
                  <MenuItem value=""><em>{t('common.none')}</em></MenuItem>
                  {countryOptions.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
                <TextField
                  label={t('userManagement.employee.fields.postalCode', { ns: 'admin' })}
                  value={employeeInfo.postal_code}
                  onChange={(e) => setEmployeeInfo({ ...employeeInfo, postal_code: e.target.value })}
                  fullWidth
                />
              <FormControl fullWidth>
                <InputLabel>{t('userManagement.employee.fields.position', { ns: 'admin' })}</InputLabel>
                <Select
                  value={employeeInfo.position}
                  label={t('userManagement.employee.fields.position', { ns: 'admin' })}
                  onChange={(e) => setEmployeeInfo({ ...employeeInfo, position: e.target.value })}
                >
                  <MenuItem value=""><em>{t('common.none')}</em></MenuItem>
                  {positionOptions.map((p) => (
                    <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>{t('userManagement.employee.fields.title', { ns: 'admin' })}</InputLabel>
                <Select
                  value={employeeInfo.title}
                  label={t('userManagement.employee.fields.title', { ns: 'admin' })}
                  onChange={(e) => setEmployeeInfo({ ...employeeInfo, title: e.target.value })}
                >
                  <MenuItem value=""><em>{t('common.none')}</em></MenuItem>
                  {titleOptions.map((tOption) => (
                    <MenuItem key={tOption.value} value={tOption.value}>{tOption.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>{t('userManagement.employee.fields.manager', { ns: 'admin' })}</InputLabel>
                <Select
                  value={employeeInfo.manager_id}
                  label={t('userManagement.employee.fields.manager', { ns: 'admin' })}
                  onChange={(e) => setEmployeeInfo({ ...employeeInfo, manager_id: e.target.value })}
                >
                  <MenuItem value=""><em>{t('common.none')}</em></MenuItem>
                  {users
                    .filter((u) => !selectedUser || u.id !== selectedUser.id)
                    .map((u) => (
                      <MenuItem key={u.id} value={u.id}>{`${u.full_name} (${u.email})`}</MenuItem>
                    ))}
                </Select>
              </FormControl>
                <TextField
                  label={t('userManagement.employee.fields.hireDate', { ns: 'admin' })}
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={employeeInfo.hire_date}
                  onChange={(e) => setEmployeeInfo({ ...employeeInfo, hire_date: e.target.value })}
                  fullWidth
                />
              <FormControl fullWidth>
                <InputLabel>{t('userManagement.employee.fields.contractType', { ns: 'admin' })}</InputLabel>
                <Select
                  value={employeeInfo.contract_type}
                  label={t('userManagement.employee.fields.contractType', { ns: 'admin' })}
                  onChange={(e) => setEmployeeInfo({ ...employeeInfo, contract_type: e.target.value })}
                >
                  <MenuItem value=""><em>{t('common.none')}</em></MenuItem>
                  {contractOptions.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('userManagement.buttons.cancel', { ns: 'admin' })}</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={
              !formData.email ||
              !formData.full_name ||
              (dialogMode === 'create' && !formData.password)
            }
          >
            {dialogMode === 'create' ? t('userManagement.buttons.create', { ns: 'admin' }) : t('userManagement.buttons.update', { ns: 'admin' })}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

